import { type Locale, SUPPORTED_LOCALES, translate } from "./index.js";

export type RuntimeIdentityMode = "start" | "restart";

export interface RuntimeEntryCopyOptions {
  fullscreenActive: boolean;
  installPromptAvailable: boolean;
}

export interface RuntimeEntryCopy {
  htmlLang: string;
  documentTitle: string;
  appTitle: string;
  identity: {
    label: string;
    placeholder: string;
    submit: string;
  };
  leaderboard: {
    title: string;
    empty: string;
    mobileToggle: string;
  };
  controls: {
    mobileBoost: string;
    soundOn: string;
    soundOff: string;
    mouseLockOff: string;
    mouseLockOn: string;
    mouseLockTitleOff: string;
    mouseLockTitleOn: string;
  };
  pwa: {
    install: string;
    fullscreen: string;
    dismissLabel: string;
    tip: string;
  };
  contentLinks: {
    label: string;
    library: RuntimeContentLink;
    privacy: RuntimeContentLink;
    feedback: RuntimeContentLink;
    discord: RuntimeContentLink;
  };
  localeLabel: string;
}

export interface RuntimeContentLink {
  label: string;
  href: string;
}

export interface LocaleSwitcherItem {
  locale: Locale;
  label: string;
  active: boolean;
}

export function getRuntimeEntryCopy(
  locale: Locale,
  mode: RuntimeIdentityMode,
  options: RuntimeEntryCopyOptions,
): RuntimeEntryCopy {
  return {
    htmlLang: locale,
    documentTitle: translate(locale, "app.runtimeTitle"),
    appTitle: translate(locale, "app.shortTitle"),
    identity: {
      label: translate(locale, "identity.label"),
      placeholder: translate(locale, "identity.placeholder"),
      submit: translate(
        locale,
        mode === "restart" ? "identity.restart" : "identity.start",
      ),
    },
    leaderboard: {
      title: translate(locale, "leaderboard.title"),
      empty: translate(locale, "leaderboard.empty"),
      mobileToggle: translate(locale, "leaderboard.mobileToggle"),
    },
    controls: {
      mobileBoost: translate(locale, "controls.mobileBoost"),
      soundOn: translate(locale, "controls.soundOn"),
      soundOff: translate(locale, "controls.soundOff"),
      mouseLockOff: translate(locale, "controls.mouseLockOff"),
      mouseLockOn: translate(locale, "controls.mouseLockOn"),
      mouseLockTitleOff: translate(locale, "controls.mouseLockTitleOff"),
      mouseLockTitleOn: translate(locale, "controls.mouseLockTitleOn"),
    },
    pwa: {
      install: translate(locale, "pwa.install"),
      fullscreen: translate(
        locale,
        options.fullscreenActive ? "pwa.exitFullscreen" : "pwa.fullscreen",
      ),
      dismissLabel: translate(locale, "pwa.dismissLabel"),
      tip: translate(
        locale,
        options.installPromptAvailable ? "pwa.installTip" : "pwa.manualTip",
      ),
    },
    contentLinks: getRuntimeContentLinks(locale),
    localeLabel: translate(locale, "locale.label"),
  };
}

function getRuntimeContentLinks(
  locale: Locale,
): RuntimeEntryCopy["contentLinks"] {
  const contentBase = locale === "en" ? "/en" : "/zh-CN";
  return {
    label: translate(locale, "identity.contentLabel"),
    library: {
      label: translate(locale, "identity.libraryLink"),
      href: `${contentBase}/guide.html`,
    },
    privacy: {
      label: translate(locale, "identity.privacyLink"),
      href: `${contentBase}/privacy.html`,
    },
    feedback: {
      label: translate(locale, "identity.feedbackLink"),
      href: "mailto:feedback@xiuxian.io",
    },
    discord: {
      label: translate(locale, "identity.discordLink"),
      href: "https://discord.gg/dbYRfK2zQh",
    },
  };
}

export function buildRuntimeLocaleUrl(
  currentUrl: string,
  locale: Locale,
): string {
  const url = new URL(currentUrl);
  url.pathname = locale === "en" ? "/en/play" : "/zh-CN/play";
  url.searchParams.delete("lang");
  url.hash = "";
  return `${url.pathname}${url.search}`;
}

export function getLocaleSwitcherItems(
  activeLocale: Locale,
): LocaleSwitcherItem[] {
  return SUPPORTED_LOCALES.map((locale) => ({
    locale,
    label: translate(locale, locale === "zh-CN" ? "locale.zhCN" : "locale.en"),
    active: locale === activeLocale,
  }));
}
